import { useState, useEffect } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  Badge,
  Spinner,
  Center,
  EmptyState,
  Flex,
} from "@chakra-ui/react";
import { FiBell, FiCheck, FiTrash2, FiCheckCircle } from "react-icons/fi";
import api from "../lib/api";
import type {
  Notification,
  NotificationListResponse,
  APIResponse,
} from "../types";
import { toaster } from "../components/ui/toaster";

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState<"all" | "unread" | "read">("all");
  const pageSize = 20;

  useEffect(() => {
    fetchNotifications();
  }, [page, filter]);

  useEffect(() => {
    fetchUnreadCount();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const isReadParam =
        filter === "all" ? "" : `&is_read=${filter === "read"}`;
      const response = await api.get<APIResponse<NotificationListResponse>>(
        `/notifications?page=${page}&page_size=${pageSize}${isReadParam}`,
      );

      setNotifications(response.data.data.notifications);
      setUnreadCount(response.data.data.unread_count);
      setTotalPages(Math.ceil(response.data.data.total / pageSize));
    } catch (error: any) {
      toaster.create({
        title: "Failed to load notifications",
        description: error.response?.data?.message || "An error occurred",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get<APIResponse<{ unread_count: number }>>(
        "/notifications/unread-count",
      );
      setUnreadCount(response.data.data.unread_count);
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  const markAsRead = async (notificationId: number) => {
    try {
      await api.put(`/notifications/${notificationId}/read`, { is_read: true });

      setNotifications(
        notifications.map((n) =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n,
        ),
      );

      setUnreadCount((prev) => Math.max(0, prev - 1));

      toaster.create({
        title: "Marked as read",
        type: "success",
      });
    } catch (error: any) {
      toaster.create({
        title: "Failed to mark as read",
        description: error.response?.data?.message || "An error occurred",
        type: "error",
      });
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put("/notifications/mark-all-read");

      setNotifications(
        notifications.map((n) => ({
          ...n,
          is_read: true,
          read_at: new Date().toISOString(),
        })),
      );
      setUnreadCount(0);

      toaster.create({
        title: "All notifications marked as read",
        type: "success",
      });
    } catch (error: any) {
      toaster.create({
        title: "Failed to mark all as read",
        description: error.response?.data?.message || "An error occurred",
        type: "error",
      });
    }
  };

  const deleteNotification = async (notificationId: number) => {
    try {
      await api.delete(`/notifications/${notificationId}`);

      const deletedNotif = notifications.find((n) => n.id === notificationId);
      setNotifications(notifications.filter((n) => n.id !== notificationId));

      if (deletedNotif && !deletedNotif.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      toaster.create({
        title: "Notification deleted",
        type: "success",
      });
    } catch (error: any) {
      toaster.create({
        title: "Failed to delete notification",
        description: error.response?.data?.message || "An error occurred",
        type: "error",
      });
    }
  };

  const getNotificationTypeColor = (type: string) => {
    switch (type) {
      case "success":
        return "green";
      case "error":
        return "red";
      case "warning":
        return "orange";
      default:
        return "blue";
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "ingestion":
        return "Data Ingestion";
      case "normalization":
        return "Drug Normalization";
      case "system":
        return "System";
      default:
        return category;
    }
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <VStack gap={6} align="stretch" p={3}>
      {/* Header */}
      <Box>
        <Heading size="xl">Notifications</Heading>
        <Text color="gray.600" mt={1}>
          Stay updated with system activities and alerts
        </Text>
      </Box>

      {/* Actions Bar */}
      <HStack justify="space-between" wrap={"wrap"}>
        <HStack gap={2}>
          <Button
            size="sm"
            variant={filter === "all" ? "solid" : "outline"}
            onClick={() => {
              setFilter("all");
              setPage(1);
            }}>
            All
          </Button>
          <Button
            size="sm"
            variant={filter === "unread" ? "solid" : "outline"}
            onClick={() => {
              setFilter("unread");
              setPage(1);
            }}>
            Unread ({unreadCount})
          </Button>
          <Button
            size="sm"
            variant={filter === "read" ? "solid" : "outline"}
            onClick={() => {
              setFilter("read");
              setPage(1);
            }}>
            Read
          </Button>
        </HStack>

        {unreadCount > 0 && (
          <Button size="sm" variant="outline" onClick={markAllAsRead}>
            <FiCheckCircle />
            Mark All as Read
          </Button>
        )}
      </HStack>

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <EmptyState.Root>
          <EmptyState.Content>
            <EmptyState.Indicator>
              <FiBell size={48} />
            </EmptyState.Indicator>
            <EmptyState.Title>No notifications</EmptyState.Title>
            <EmptyState.Description>
              {filter === "unread"
                ? "You're all caught up! No unread notifications."
                : filter === "read"
                  ? "No read notifications yet."
                  : "You have no notifications at this time."}
            </EmptyState.Description>
          </EmptyState.Content>
        </EmptyState.Root>
      ) : (
        <VStack gap={3} align="stretch">
          {notifications.map((notification) => (
            <Card.Root
              key={notification.id}
              bg={notification.is_read ? "white" : "blue.50"}
              borderLeft="4px"
              borderLeftColor={getNotificationTypeColor(
                notification.notification_type,
              )}>
              <Card.Body>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify={{ md: "space-between" }}
                  align={{ base: "stretch", md: "start" }}
                  gap={3}>
                  <Box flex={1}>
                    <HStack gap={2} mb={2}>
                      <Badge
                        colorPalette={getNotificationTypeColor(
                          notification.notification_type,
                        )}>
                        {notification.notification_type.toUpperCase()}
                      </Badge>
                      <Badge variant="outline">
                        {getCategoryLabel(notification.category)}
                      </Badge>
                      {!notification.is_read && (
                        <Badge colorPalette="blue">New</Badge>
                      )}
                    </HStack>

                    <Heading size="sm" mb={2} wordBreak="break-word">
                      {notification.title}
                    </Heading>

                    <Text
                      fontSize="sm"
                      color="gray.700"
                      whiteSpace="pre-wrap"
                      wordBreak="break-word"
                      mb={2}>
                      {notification.message}
                    </Text>

                    <Text fontSize="xs" color="gray.500">
                      {new Date(notification.created_at).toLocaleString()}
                    </Text>
                  </Box>

                  <HStack
                    gap={2}
                    wrap={"wrap"}
                    alignSelf={{ base: "flex-end", md: "auto" }}>
                    {!notification.is_read && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => markAsRead(notification.id)}
                        title="Mark as read">
                        <FiCheck />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      colorPalette="red"
                      onClick={() => deleteNotification(notification.id)}
                      title="Delete">
                      <FiTrash2 />
                    </Button>
                  </HStack>
                </Flex>
              </Card.Body>
            </Card.Root>
          ))}
        </VStack>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <HStack justify="center" gap={2}>
          <Button
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}>
            Previous
          </Button>
          <Text fontSize="sm" mx={4}>
            Page {page} of {totalPages}
          </Text>
          <Button
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}>
            Next
          </Button>
        </HStack>
      )}
    </VStack>
  );
}
